import {
  Pause,
  PlayArrowOutlined,
  RocketLaunchOutlined,
  Visibility,
} from '@mui/icons-material'
import {
  Box,
  Button,
  Skeleton,
  Stack,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { ReactNode, useCallback, useMemo, useState } from 'react'
import { toast } from 'react-toastify'

import CancelButton from '~/components/createEdit/cancelButton'
import SaveButton from '~/components/createEdit/saveButton'
import useGetAgentCentralized from '~/features/agents/hooks/useGetAgentCentralized'
import ActionErrorsDisplay from '~/features/campaigns/components/ofuManageCampaign/actionErrorsDisplay'
import CampaignGoLiveAction from '~/features/campaigns/components/ofuManageCampaign/campaignGoLiveAction'
import CampaignMoreActions from '~/features/campaigns/components/ofuManageCampaign/campaignMoreActions'
import OFUSimulate from '~/features/campaigns/components/ofuSimulate'
import useGoLiveValidationErrorsWithSteps, {
  SCHEDULE_STEPS_LOADING_MESSAGE,
} from '~/features/campaigns/hooks/useGoLiveValidationErrorsWithSteps'
import useUpdateCampaign from '~/features/campaigns/hooks/useUpdateCampaign'
import {
  ECampaignBuilderFormKeys,
  ECampaignBuilderStepIds,
  ECampaignExecutionStatus,
  ECampaignKeys,
  TCampaign,
  TCampaignAgentJunction,
  TCampaignFormFields,
} from '~/features/campaigns/models'
import {
  getCampaignAssistantDisplayName,
  getMissingRequiredAgentMetadataForJunction,
  hasRequiredAgentMetadataForJunction,
  TGoLiveValidationErrorWithStep,
} from '~/features/campaigns/utils'
import { getCampaignJunctions } from '~/features/campaigns/utils/campaignJunctions'
import useGetSelectedCampaign from '~/hooks/useGetSelectedCampaign'
import { ETestId } from '~/models/tests'
import rem from '~/utils/ui'

export default function OfuManageCampaignActions() {
  const theme = useTheme()
  const isCompactActions = useMediaQuery('(max-width:1200px)')
  const { selectedAgent } = useGetAgentCentralized()
  const { isLoading, status, isArchived, campaign } = useGetSelectedCampaign()

  const {
    isScheduleStepsReady,
    validationErrorsWithSteps: simulateValidationErrorsWithSteps,
  } = useGoLiveValidationErrorsWithSteps(campaign, { selectedAgent })
  const fallbackCampaignAgentId = useMemo(
    () => String(campaign?.[ECampaignKeys.agentId] || '').trim(),
    [campaign],
  )
  const hasAnyAssistantReadyForSimulation = useMemo(() => {
    const junctions = getCampaignJunctions(campaign as TCampaign | null)
    if (junctions.length === 0) {
      return Boolean(fallbackCampaignAgentId)
    }
    return junctions.some((junction) =>
      hasRequiredAgentMetadataForJunction({
        campaign: campaign as TCampaign | null,
        junction,
      }),
    )
  }, [campaign, fallbackCampaignAgentId])
  const simulationEligibleAgentIds = useMemo(() => {
    const junctions = getCampaignJunctions(campaign as TCampaign | null)
    if (junctions.length === 0) {
      return fallbackCampaignAgentId ? [fallbackCampaignAgentId] : []
    }

    return junctions
      .filter((junction) =>
        hasRequiredAgentMetadataForJunction({
          campaign: campaign as TCampaign | null,
          junction,
        }),
      )
      .map((junction) => String(junction.agentId || junction.Agent?.id || ''))
      .filter(Boolean)
  }, [campaign, fallbackCampaignAgentId])
  const getJunctionMetadataErrors = useCallback(
    (junction: TCampaignAgentJunction): TGoLiveValidationErrorWithStep[] =>
      getMissingRequiredAgentMetadataForJunction({
        campaign: campaign as TCampaign | null,
        junction,
      }).map((field) => {
        const fieldKey = String(
          field.friendlyName || field.variable || '',
        ).trim()
        return {
          message: `${getCampaignAssistantDisplayName(junction)}: ${field.friendlyName || field.variable || 'Required field'}`,
          friendlyField: `${getCampaignAssistantDisplayName(junction)}: ${field.friendlyName || field.variable || 'Required field'}`,
          stepId: ECampaignBuilderStepIds.multiAssistantSetup,
          fieldPath: `${ECampaignBuilderFormKeys.sharedCampaignAgentSettings}.${junction.agentId}.locationFields.${fieldKey}`,
        }
      }),
    [campaign],
  )
  const allAssistantsMetadataErrorsWithSteps = useMemo(
    () =>
      getCampaignJunctions(campaign as TCampaign | null).flatMap((junction) =>
        getJunctionMetadataErrors(junction),
      ),
    [campaign, getJunctionMetadataErrors],
  )
  const allValidationErrorsWithSteps = useMemo(
    () => [
      ...simulateValidationErrorsWithSteps,
      ...allAssistantsMetadataErrorsWithSteps,
    ],
    [allAssistantsMetadataErrorsWithSteps, simulateValidationErrorsWithSteps],
  )

  const canSimulate =
    isScheduleStepsReady &&
    simulateValidationErrorsWithSteps.length === 0 &&
    hasAnyAssistantReadyForSimulation

  const isPaused = status === ECampaignExecutionStatus.paused
  const isDraft = status === ECampaignExecutionStatus.notStarted
  const isActive = status === ECampaignExecutionStatus.inProgress

  const { mutate: updateCampaign, isPending: isUpdating } = useUpdateCampaign(
    campaign?.id || '',
  )
  const canGoLive =
    !isArchived &&
    !isUpdating &&
    isScheduleStepsReady &&
    simulateValidationErrorsWithSteps.length === 0 &&
    hasAnyAssistantReadyForSimulation

  const simulateTooltipContent: ReactNode = useMemo(() => {
    if (!canSimulate || isArchived || isUpdating) {
      if (isArchived) {
        return 'Campaign is archived.'
      }
      if (isUpdating) {
        return 'Updating...'
      }
      if (!isScheduleStepsReady) {
        return SCHEDULE_STEPS_LOADING_MESSAGE
      }
      if (!hasAnyAssistantReadyForSimulation) {
        return 'At least one assistant must have all required location fields completed.'
      }
      return (
        <ActionErrorsDisplay
          validationErrorsWithSteps={allValidationErrorsWithSteps}
          campaignId={campaign?.id}
          linkColor={theme.palette.common.white}
          showLabel
        />
      )
    }
    return ''
  }, [
    allValidationErrorsWithSteps,
    campaign?.id,
    canSimulate,
    hasAnyAssistantReadyForSimulation,
    isArchived,
    isScheduleStepsReady,
    isUpdating,
    theme.palette.common.white,
  ])

  const goLiveTooltipContent: ReactNode = useMemo(() => {
    if (!canGoLive) {
      if (isArchived) {
        return 'Campaign is archived.'
      }
      if (isUpdating) {
        return 'Updating...'
      }
      if (!isScheduleStepsReady) {
        return SCHEDULE_STEPS_LOADING_MESSAGE
      }
      if (allValidationErrorsWithSteps.length > 0) {
        return (
          <ActionErrorsDisplay
            validationErrorsWithSteps={allValidationErrorsWithSteps}
            campaignId={campaign?.id}
            linkColor={theme.palette.common.white}
            showLabel
          />
        )
      }
      return 'Select at least one assistant before going live.'
    }
    return ''
  }, [
    allValidationErrorsWithSteps,
    campaign?.id,
    canGoLive,
    isArchived,
    isScheduleStepsReady,
    isUpdating,
    theme.palette.common.white,
  ])

  const updateCampaignStatus = useCallback(
    (newStatus: ECampaignExecutionStatus) => {
      const isStarting = newStatus === ECampaignExecutionStatus.inProgress
      updateCampaign(
        {
          [ECampaignKeys.executionStatus]: newStatus,
        },
        {
          onSuccess: () => {
            toast.success(
              `Campaign ${isStarting ? 'started' : 'paused'} successfully`,
            )
          },
          onError: () => {
            toast.error(
              `Failed to ${isStarting ? 'start' : 'pause'} campaign. Please try again.`,
            )
          },
        },
      )
    },
    [updateCampaign],
  )

  const handleUpdateCampaignStatus = useCallback(
    (newCampaignStatus: ECampaignExecutionStatus) => {
      updateCampaignStatus(newCampaignStatus)
    },
    [updateCampaignStatus],
  )

  const [openSimulateFromMenu, setOpenSimulateFromMenu] = useState<
    (() => void) | null
  >(null)
  const [openGoLiveFromMenu, setOpenGoLiveFromMenu] = useState<
    (() => void) | null
  >(null)
  const registerSimulateOpenHandler = useCallback((openHandler: () => void) => {
    setOpenSimulateFromMenu(() => openHandler)
  }, [])
  const registerGoLiveOpenHandler = useCallback((openHandler: () => void) => {
    setOpenGoLiveFromMenu(() => openHandler)
  }, [])

  const compactActions = useMemo(
    () => [
      ...(isActive && !isArchived
        ? [
            {
              key: 'pause',
              label: 'Pause',
              icon: <Pause fontSize="small" sx={{ mr: 2 }} />,
              onClick: () =>
                handleUpdateCampaignStatus(ECampaignExecutionStatus.paused),
              disabled: isArchived || isUpdating,
              render: (onClick: () => void) => (
                <Button
                  variant="secondaryModern"
                  startIcon={<Pause />}
                  sx={{ height: rem(32), width: '100%' }}
                  disabled={isArchived || isUpdating}
                  onClick={onClick}
                >
                  Pause
                </Button>
              ),
            },
          ]
        : []),
      ...(campaign
        ? [
            {
              key: 'simulate',
              label: 'Simulate',
              icon: <Visibility fontSize="small" sx={{ mr: 2 }} />,
              onClick: () => openSimulateFromMenu?.(),
              disabled:
                isArchived ||
                isUpdating ||
                !canSimulate ||
                !openSimulateFromMenu,
              render: (onClick: () => void) => (
                <Tooltip
                  title={simulateTooltipContent}
                  placement="left"
                  leaveDelay={400}
                  disableInteractive={false}
                >
                  <Box
                    component="span"
                    sx={{ display: 'inline-block', width: '100%' }}
                  >
                    <Button
                      variant="secondaryModern"
                      startIcon={<Visibility />}
                      onClick={onClick}
                      sx={{ height: rem(32), width: '100%' }}
                      disabled={
                        isArchived ||
                        isUpdating ||
                        !canSimulate ||
                        !openSimulateFromMenu
                      }
                    >
                      Simulate
                    </Button>
                  </Box>
                </Tooltip>
              ),
            },
          ]
        : []),
      ...(isDraft
        ? [
            {
              key: 'go-live',
              label: 'Go live',
              icon: <RocketLaunchOutlined fontSize="small" sx={{ mr: 2 }} />,
              onClick: () => openGoLiveFromMenu?.(),
              disabled: !canGoLive || !openGoLiveFromMenu,
              render: (onClick: () => void) => (
                <Tooltip
                  title={goLiveTooltipContent}
                  placement="left"
                  leaveDelay={400}
                  disableInteractive={false}
                >
                  <Box
                    component="span"
                    sx={{ display: 'inline-block', width: '100%' }}
                  >
                    <SaveButton
                      data-testid={ETestId.CampaignOfuManageGoLive}
                      label="Go live"
                      sx={{ height: rem(32), width: '100%' }}
                      startIcon={<RocketLaunchOutlined />}
                      disabled={!canGoLive || !openGoLiveFromMenu}
                      onClick={onClick}
                    />
                  </Box>
                </Tooltip>
              ),
            },
          ]
        : []),
      ...(isPaused
        ? [
            {
              key: 'resume',
              label: 'Resume',
              icon: <PlayArrowOutlined fontSize="small" sx={{ mr: 2 }} />,
              onClick: () =>
                handleUpdateCampaignStatus(ECampaignExecutionStatus.inProgress),
              disabled: isArchived || isUpdating,
              render: (onClick: () => void) => (
                <Button
                  startIcon={<PlayArrowOutlined />}
                  variant="primaryModern"
                  sx={{ height: rem(32), width: '100%' }}
                  disabled={isArchived || isUpdating}
                  onClick={onClick}
                >
                  Resume
                </Button>
              ),
            },
          ]
        : []),
    ],
    [
      campaign,
      canGoLive,
      canSimulate,
      handleUpdateCampaignStatus,
      isActive,
      isArchived,
      isDraft,
      isPaused,
      isUpdating,
      openGoLiveFromMenu,
      openSimulateFromMenu,
      simulateTooltipContent,
      goLiveTooltipContent,
    ],
  )

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      {isLoading && (
        <>
          {[100, 100, 32].map((width, index) => (
            <Skeleton
              key={`loading-action-${index.toString()}`}
              variant="rounded"
              width={rem(width)}
              height={rem(32)}
            />
          ))}
          <Skeleton variant="rounded" width={rem(100)} height={rem(32)} />
          <Skeleton variant="rounded" width={rem(32)} height={rem(32)} />
        </>
      )}
      {!isLoading && (
        <>
          {isActive && !isArchived && !isCompactActions && (
            <CancelButton
              data-testid={ETestId.CampaignOfuManagePause}
              startIcon={<Pause />}
              label="Pause"
              sx={{ height: rem(32) }}
              disabled={isArchived || isUpdating}
              onClick={() =>
                handleUpdateCampaignStatus(ECampaignExecutionStatus.paused)
              }
            />
          )}

          {campaign && (
            <Tooltip
              title={simulateTooltipContent}
              placement="bottom"
              leaveDelay={400}
              disableInteractive={false}
            >
              <Box>
                <OFUSimulate
                  isDisabled={isArchived || isUpdating || !canSimulate}
                  campaign={campaign as unknown as TCampaignFormFields}
                  allowedAgentIds={simulationEligibleAgentIds}
                  variant={isCompactActions ? 'hidden' : 'newUi'}
                  onRegisterOpenHandler={registerSimulateOpenHandler}
                />
              </Box>
            </Tooltip>
          )}

          {isPaused && !isCompactActions && (
            <SaveButton
              startIcon={<PlayArrowOutlined />}
              label="Resume"
              sx={{ height: rem(32) }}
              disabled={isArchived || isUpdating}
              onClick={() =>
                handleUpdateCampaignStatus(ECampaignExecutionStatus.inProgress)
              }
            />
          )}

          {isDraft && (
            <CampaignGoLiveAction
              hideButton={isCompactActions}
              onRegisterOpenHandler={registerGoLiveOpenHandler}
            />
          )}

          <CampaignMoreActions
            extraActions={isCompactActions ? compactActions : undefined}
          />
        </>
      )}
    </Stack>
  )
}
